import { ContractListItemResponse } from 'compliance-api/generated';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContractStatusBadge } from '@/modules/admin-dashboard/components/contractManagement/ContractStatusBadge.tsx';
import { formatContractCategoryLabel } from '@/modules/admin-dashboard/components/contractManagement/contractCategory.ts';
import { useContractsListQuery } from '@/modules/admin-dashboard/queries/contract-management.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { EmptyListState, SearchField, Spinner } from 'ui';

export const PartyContractsTab = ({ partyId }: { partyId: number }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const { data, isFetching, isLoading } = useContractsListQuery({
    page: 1,
    page_size: 30,
    party_id: partyId,
    search: search || undefined,
  });

  const contracts = useMemo(() => (data?.pages || []).flatMap(page => page.data), [data?.pages]);

  const handleOpenContract = (contractId: number) => {
    navigate({
      pathname: adminDashboardPaths.dynamic.contract(contractId),
      search: location.search,
    });
  };

  return (
    <div className="space-y-4">
      {!!contracts.length && (
        <SearchField
          placeholder="Search"
          value={search}
          onChange={setSearch}
          allowClear
          isLoading={isFetching && !isLoading}
          className="w-full"
        />
      )}

      <div className="-mx-5">
        {!!contracts.length && (
          <div className="grid grid-cols-[1.4fr_0.8fr_1fr] bg-gray-100 px-5 py-2 text-xs leading-5 text-gray-500">
            <span>Name</span>
            <span>Status</span>
            <span>Category</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex min-h-[120px] items-center justify-center">
            <Spinner size="md" variant="primary" />
          </div>
        ) : contracts.length > 0 ? (
          <div>
            {contracts.map(contract => (
              <PartyContractRow
                key={contract.id}
                contract={contract}
                onClick={() => handleOpenContract(contract.id)}
              />
            ))}
          </div>
        ) : (
          <EmptyListState
            title="No contracts found"
            description="This party is not linked to any contracts yet."
            isLoading={false}
            isSearchUsed={!!search}
            itemsCount={contracts.length}
            classes={{
              container: 'min-h-[500px]',
              title: 'text-xl text-[28px]',
              image: 'h-20 w-20',
              description: 'text-sm',
            }}
          />
        )}
      </div>
    </div>
  );
};

const PartyContractRow = ({
  contract,
  onClick,
}: {
  contract: ContractListItemResponse;
  onClick: () => void;
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    }}
    className="grid grid-cols-[1.4fr_0.8fr_1fr] border-b border-gray-200 px-5 py-4 text-sm leading-[22px] text-gray-900 transition-colors hover:cursor-pointer hover:bg-gray-50"
  >
    <span className="min-w-0 truncate font-medium">{contract.contract_name || '-'}</span>
    <span>
      <ContractStatusBadge status={contract.status} />
    </span>
    <span className="min-w-0 truncate">{formatContractCategoryLabel(contract.category)}</span>
  </div>
);
